import { useState } from 'react';
import {
  components,
  type ContainerProps,
  type CSSObjectWithLabel,
  type IndicatorSeparatorProps,
  type MenuListProps,
} from 'react-select';

import type { StudentSearch } from '~entities/student';
import { useGetAllStudentsWithSearch } from '~entities/student/api/get-all-students-with-search';

import {
  SELECT_MENU_PORTAL_CLASS,
  defaultClassNames,
  defaultStyles,
} from '~shared/components/select/helper';
import Select from '~shared/components/select/select';
import { calculateAgeFromISO } from '~shared/helpers/datetime';
import { cn } from '~shared/shadcn/lib/utils';

export interface StudentSelectorOption {
  label: string;
  value: number | string;
  ageYears: number;
  ageMonths: number;
}

export interface StudentSelectorProps {
  // value: string | number;
  onChange: (value: StudentSearch | undefined | null) => void;
  currentStudentId?: number;
  currentStudentName?: string;
  currentStudentYearAge?: number;
  currentStudentMonthsAge?: number;
  isDisabled?: boolean;
  isOptionDisabled?: (
    option: StudentSelectorOption,
    selectValue: ReadonlyArray<StudentSelectorOption>,
  ) => boolean;
  placeholder?: string;
  studentCount?: number;
  className?: string;
  /**
   * Portal the menu (required inside Radix dialogs so the menu is not clipped).
   * Prefer `menuPortalTarget` set to the DialogContent element so option clicks
   * stay "inside" the dialog for Radix dismiss handling.
   * Pass `null` while the dialog ref is resolving — do not default to body.
   */
  menuInPortal?: boolean;
  /** Element to portal into. Defaults to document.body only when omitted. */
  menuPortalTarget?: HTMLElement | null;
}

const SelectContainer = ({ children, ...props }: ContainerProps) => {
  return (
    <components.SelectContainer
      {...props}
      className={cn(
        'z-20 w-56 rounded-md border-2 border-secondary',
        props.className,
      )}
    >
      {children}
    </components.SelectContainer>
  );
};

const IndicatorSeparator = ({ innerProps }: IndicatorSeparatorProps) => {
  return (
    <span className='my-2 w-px self-stretch bg-secondary' {...innerProps} />
  );
};

const StudentMenuList = ({ children, innerRef, innerProps }: MenuListProps) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={cn(
        'max-h-60 overflow-y-auto overscroll-contain',
        innerProps.className,
      )}
      style={{ ...innerProps.style, maxHeight: 240, overflowY: 'auto' }}
      onWheel={(event) => {
        event.stopPropagation();
        innerProps.onWheel?.(event);
      }}
    >
      {children}
    </div>
  );
};

export function StudentSelector({
  onChange,
  currentStudentId,
  currentStudentName,
  isDisabled = false,
  currentStudentMonthsAge,
  currentStudentYearAge,
  isOptionDisabled,
  placeholder = 'Enroll Student in Class',
  studentCount = 10000,
  className,
  menuInPortal = false,
  menuPortalTarget = null,
}: StudentSelectorProps) {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [selectedStudent, setSelectedStudent] = useState<
    StudentSelectorOption | null | undefined
  >(() => {
    if (currentStudentId && currentStudentName) {
      return {
        label: currentStudentName,
        value: currentStudentId,
        ageYears: currentStudentYearAge ?? 0,
        ageMonths: currentStudentMonthsAge ?? 0,
      };
    }
    return null;
  });

  const { data: studentsData, isFetching } = useGetAllStudentsWithSearch({
    name: searchQuery,
    limit: studentCount,
    page: 1,
  });

  const studentOptions: Array<StudentSelectorOption> =
    studentsData?.data.students.map((student) => {
      const age = calculateAgeFromISO(student.dob);
      return {
        label: `${student.firstName} ${student.lastName} (${age.years}y ${age.months}m)`,
        value: student._id,
        ageYears: age.years,
        ageMonths: age.months,
      };
    }) ?? [];

  const resolvedPortalTarget = menuInPortal
    ? // Explicit null = caller is still resolving a DialogContent ref; do not
      // fall back to body (that reintroduces Radix outside-click dismiss).
      menuPortalTarget === undefined
      ? typeof document !== 'undefined'
        ? document.body
        : null
      : menuPortalTarget
    : null;

  const customStyles = {
    ...defaultStyles,
    control: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
      ...base,
      backgroundColor: '#ffcf9db', // subtle yellow tint for the whole container
    }),
    placeholder: (base: CSSObjectWithLabel) => ({
      ...base,
      fontWeight: 'bold',
    }),
    ...(menuInPortal
      ? {
          menuPortal: (base: CSSObjectWithLabel) => ({
            ...base,
            zIndex: 70,
          }),
        }
      : {}),
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Select
        isDisabled={isDisabled}
        isClearable
        value={selectedStudent}
        onChange={(option) => {
          const opt = option as StudentSelectorOption | null | undefined;
          setSelectedStudent(opt);
          const matched = studentsData?.data.students.find(
            (s) => String(s._id) === String(opt?.value ?? ''),
          );
          onChange(matched);
        }}
        placeholder={placeholder}
        options={studentOptions}
        isLoading={isFetching}
        isOptionDisabled={(option, selectValue) => {
          if (!isOptionDisabled) return false;

          return isOptionDisabled(
            option as StudentSelectorOption,
            selectValue as ReadonlyArray<StudentSelectorOption>,
          );
        }}
        components={{
          SelectContainer,
          IndicatorSeparator,
          MenuList: StudentMenuList,
        }}
        className={cn('z-50 w-64', className)}
        onInputChange={(value, { action }) => {
          if (action === 'input-change') {
            setSearchQuery(value);
          }
        }}
        styles={customStyles}
        menuPosition={menuInPortal ? 'fixed' : undefined}
        menuPortalTarget={resolvedPortalTarget}
        classNames={
          menuInPortal
            ? {
                ...defaultClassNames,
                menuPortal: (state) =>
                  cn(
                    defaultClassNames.menuPortal?.(state),
                    SELECT_MENU_PORTAL_CLASS,
                  ),
              }
            : undefined
        }
      />
    </div>
  );
}
